import React, { useContext, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import '../styles/Header.scss'
import { AuthContext } from '../context/auth/authState'
import { ContactContext, EventsContext, LanguageContext } from '../context'
import { AUTH_PROMPT_TYPE } from '../utils/constants'
import { Person, PersonOutline } from '@mui/icons-material'
import { LOCALE_OPTIONS } from '../context/language/languageState'

function Header() {
  const { authStateLoaded, isUserAuthenticated, promptAuth } = useContext(AuthContext)
  const { promptCreateEvent } = useContext(EventsContext)
  const { t, localizedDocumentUrl, setLocale, currentLocale } = useContext(LanguageContext)
  const [showSidebar, setShowSidebar] = useState(false)

  const { openContactModal } = useContext(ContactContext)

  const [showLangDropdown, setShowLangDropdown] = useState(false)

  useEffect(() => {
    if (showSidebar) {
      document.body.classList.add("no-scroll")
    } else {
      document.body.classList.remove("no-scroll")
    }

    // Cleanup function to remove the class when the component unmounts
    return () => {
      document.body.classList.remove("no-scroll")
    }
  }, [showSidebar])

  const handlePromptAuth = (type) => {
    promptAuth(type)
    setShowSidebar(false)
  }
  
  const handleSelectLocale = (locale) => {
    setLocale(locale)
    setShowLangDropdown(false)
  }

  return (
    <>
      <div className={'overlay' + (showSidebar ? ' display' : '')} onClick={() => setShowSidebar(false)}></div>
      <div className={'sidebar w-72 md:w-80 h-full bg-indigo-700 text-white p-6' + (showSidebar ? ' display' : '')}>
        <Link to="/" onClick={() => setShowSidebar(false)} className='home-page-link'>WeCar</Link>
        <div className='lg:hidden'>
          <button onClick={promptCreateEvent} className='light w-full mt-4'>
            {`+ ${t('event.create_button')}`}
          </button>

          {authStateLoaded && !isUserAuthenticated && <ul>
            <li><a onClick={() => { handlePromptAuth(AUTH_PROMPT_TYPE.LOGIN) }}>{t('auth.connect')}</a></li>
            <li><a onClick={() => { handlePromptAuth(AUTH_PROMPT_TYPE.SIGNUP) }}>{t('auth.sign_up')}</a></li>
          </ul>}
        </div>
        {authStateLoaded && isUserAuthenticated && <div className='lg:hidden'>
          <div className='separator'></div>
          <ul>
            <li><Link to="/my-events" onClick={() => setShowSidebar(false)}>{t('header.my_events')}</Link></li>
            {/* <li><Link to="/co2" onClick={() => setShowSidebar(false)}>{t('header.my_impact')}</Link></li> */}
          </ul>
        </div>}
        <div className='separator'></div>
        <ul className='mb-8'>
          <li><Link to="/" onClick={() => setShowSidebar(false)}>{t('header.home')}</Link></li>
          <li><Link to="/news" onClick={() => setShowSidebar(false)}>{t('header.news')}</Link></li>
          <li><Link to="/about" onClick={() => setShowSidebar(false)}>{t('header.about')}</Link></li>
          <li><Link to="/faq" onClick={() => setShowSidebar(false)}>{t('header.faq')}</Link></li>
          <li><Link to="#" onClick={() => {
            openContactModal()
            setShowSidebar(false)
          }}>{t('header.contact')}</Link></li>
        </ul>
        <ul>
          <li><a target='_blank' className='small mb-2' rel='noreferrer' href={localizedDocumentUrl('application')} onClick={() => setShowSidebar(false)}>{t('footer.cgu_application')}</a></li>
          <li><a target='_blank' className='small mb-2' rel='noreferrer' href={localizedDocumentUrl('service')} onClick={() => setShowSidebar(false)}>{t('footer.cgu_service')}</a></li>
          <li><a target='_blank' className='small mb-2' rel='noreferrer' href={localizedDocumentUrl('privacy')} onClick={() => setShowSidebar(false)}>{t('footer.privacy_policy')}</a></li>
          <li><a target='_blank' className='small mb-2' rel='noreferrer' href={localizedDocumentUrl('cookies')} onClick={() => setShowSidebar(false)}>{t('footer.cookies_policy')}</a></li>
        </ul>
      </div>
      <div className='flex justify-between items-center max-w-7xl mx-auto py-6 p-4 relative mb-10 lg:mb-16'>
        <div className='flex gap-2 lg:gap-8'>
          <button onClick={() => setShowSidebar(!showSidebar)} className='p-0 bg-white'>
            <img src='/menu.svg' />
          </button>
          <div className='flex gap-4'>
            {authStateLoaded && isUserAuthenticated && <Link to={'/my-events'} className='header-links'>
              <img src='/calendar.svg' />
              <span className='hidden lg:flex'> {t('header.my_events')}</span>
            </Link>}
            {/* <Link to={'/co2'} className='header-links'>
              <img src='/nature.svg' />
              {t('header.my_impact')}
            </Link> */}
          </div>
        </div>
        <Link to={'/'} className='absolute left-2/4 top-6 -translate-x-2/4'>
          <img alt='logo' src='/JPG_1.jpg' className='w-28 lg:w-36 px-2 lg:px-1' />
        </Link>

        <div className='flex items-center'>
          <div className='flex flex-col gap-3'>
            <div className='flex flex-row'>
              <button onClick={promptCreateEvent} className='lg:w-auto w-10 h-10'>
                <img src='/add.svg' />
                <span className='hidden lg:flex'>{t('event.create_button')}</span>
              </button>
              <div className="dropdown trigger"              
                onMouseEnter={() => setShowLangDropdown(true)}
                onMouseLeave={() => setShowLangDropdown(false)}
              >
                <button
                  className="btn btn-transparent"
                  aria-label="Select language"
                >
                  <LocaleIcon />
                </button>
                <div
                  className={`dropdown menu ${showLangDropdown ? 'show' : ''}`}
                >
                  <ul>
                    {LOCALE_OPTIONS.map(option => (
                      <li key={option.value} className={option.value === currentLocale ? 'selected' : ''} onClick={() => { handleSelectLocale(option.value) }}>
                        <span className='link'>
                          <img src={`/flag-${option.value.split('-')[0]}.svg`} alt={option.label} className='w-6 h-6 inline-block mr-2' />
                          {option.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            {authStateLoaded && !isUserAuthenticated && <div className='hidden lg:block'>
              <a className='cursor-pointer link' onClick={() => promptAuth(AUTH_PROMPT_TYPE.LOGIN)}>{t('auth.connect')}</a>
              <span className='mx-4 link-separator'></span>
              <a className='cursor-pointer link' onClick={() => promptAuth(AUTH_PROMPT_TYPE.SIGNUP)}>{t('auth.sign_up')}</a>
            </div>}
          </div>
          {isUserAuthenticated && <Link to={'/my-profile'}>
            <span className='flex justify-center items-center w-9 h-9 rounded-full my-profile'>
              <PersonOutline />
            </span>
          </Link>}
        </div>
      </div>

    </>

  )
}

const LocaleIcon = () => {
  return <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#1f1f1f"><path d="M480-80q-82 0-155-31.5t-127.5-86Q143-252 111.5-325T80-480q0-83 31.5-155.5t86-127Q252-817 325-848.5T480-880q83 0 155.5 31.5t127 86q54.5 54.5 86 127T880-480q0 82-31.5 155t-86 127.5q-54.5 54.5-127 86T480-80Zm0-82q26-36 45-75t31-83H404q12 44 31 83t45 75Zm-104-16q-18-33-31.5-68.5T322-320H204q29 50 72.5 87t99.5 55Zm208 0q56-18 99.5-55t72.5-87H638q-9 38-22.5 73.5T584-178ZM170-400h136q-3-20-4.5-39.5T300-480q0-21 1.5-40.5T306-560H170q-5 20-7.5 39.5T160-480q0 21 2.5 40.5T170-400Zm216 0h188q3-20 4.5-39.5T580-480q0-21-1.5-40.5T574-560H386q-3 20-4.5 39.5T380-480q0 21 1.5 40.5T386-400Zm268 0h136q5-20 7.5-39.5T800-480q0-21-2.5-40.5T790-560H654q3 20 4.5 39.5T660-480q0 21-1.5 40.5T654-400Zm-16-240h118q-29-50-72.5-87T584-782q18 33 31.5 68.5T638-640Zm-234 0h152q-12-44-31-83t-45-75q-26 36-45 75t-31 83Zm-200 0h118q9-38 22.5-73.5T376-782q-56 18-99.5 55T204-640Z"/></svg>
}

export default Header
